<template>
  <div class="gen-history" ref="historyContainer">
    <!-- 空状态 -->
    <div class="gen-history-empty" v-if="items.length === 0 && !generating">
      <div class="empty-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b4b7c9" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
      </div>
      <div class="empty-text">{{ emptyText }}</div>
    </div>

    <!-- 对话消息列表 -->
    <div class="history-messages" v-if="items.length > 0 || generating">
      <div v-for="(entry, idx) in items" :key="'h-' + idx + '-' + (entry.historyId || '') + '-' + (entry.taskId || '')" class="history-entry">
        <!-- 结果气泡：loading -->
        <div v-if="entry.status === 'loading'" class="msg-result msg-loading-card">
          <div class="msg-meta">
            <span class="msg-meta-model" v-if="entry.modelName">{{ entry.modelName }}</span>
            <span class="msg-meta-prompt" v-if="entry.prompt">{{ entry.prompt }}</span>
          </div>
          <div class="msg-loading-bar">
            <div class="loading-spinner-sm"></div>
            <span>{{ generatingText }}</span>
          </div>
        </div>

        <!-- 结果气泡：成功 -->
        <div v-else-if="entry.status === 'completed'" class="msg-result">
          <div class="msg-meta">
            <span class="msg-meta-model" v-if="entry.modelName">{{ entry.modelName }}</span>
            <span class="msg-meta-prompt" v-if="entry.prompt">{{ entry.prompt }}</span>
          </div>
          <!-- 音乐：一条记录多轨，左右分栏（同一 historyId） -->
          <div v-if="isHistoryMusic(entry)" class="msg-music-row" :class="{ 'msg-music-row-single': getMusicTrackUrls(entry).length <= 1 }">
            <div
              v-for="(audioUrl, aIdx) in getMusicTrackUrls(entry)"
              :key="'mu-' + aIdx"
              class="msg-music-cell">
              <audio
                v-if="audioUrl"
                :src="proxiedMediaUrl(audioUrl)"
                controls
                preload="metadata"
                class="msg-music-audio"
              ></audio>
              <a
                v-if="audioUrl"
                :href="proxiedMediaUrl(audioUrl)"
                target="_blank"
                rel="noopener noreferrer"
                class="msg-result-btn msg-music-dl"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                <span>{{ downloadText }}<template v-if="getMusicTrackUrls(entry).length > 1"> {{ aIdx + 1 }}</template></span>
              </a>
            </div>
          </div>
          <!-- 视频 -->
          <video
            v-else-if="(entry.mediaType || type) === 'video'"
            :src="proxiedMediaUrl(entry.resultUrl)"
            controls
            preload="metadata"
            class="msg-media-thumb"
          ></video>
          <!-- 图片：缩略图 + 点击放大 -->
          <template v-else>
            <div v-if="getEntryImageUrls(entry).length > 1" class="msg-media-grid">
              <img
                v-for="(imgUrl, imgIdx) in getEntryImageUrls(entry)"
                :key="imgIdx"
                :src="proxiedMediaUrl(imgUrl)"
                alt=""
                class="msg-media-thumb msg-media-clickable msg-media-thumb-grid"
                @click="previewImage(proxiedMediaUrl(imgUrl))"
              />
            </div>
            <img
              v-else
              :src="proxiedMediaUrl(entry.resultUrl)"
              alt=""
              class="msg-media-thumb msg-media-clickable"
              @click="previewImage(proxiedMediaUrl(entry.resultUrl))"
            />
          </template>
          <div v-if="!isHistoryMusic(entry)" class="msg-result-actions">
            <a
              v-if="entry.resultUrl && getEntryImageUrls(entry).length <= 1"
              :href="proxiedMediaUrl(entry.resultUrl)"
              target="_blank"
              class="msg-result-btn"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              <span>{{ downloadText }}</span>
            </a>
            <a
              v-for="(imgUrl, imgIdx) in getEntryImageUrls(entry)"
              v-if="getEntryImageUrls(entry).length > 1"
              :key="'dl-' + imgIdx"
              :href="proxiedMediaUrl(imgUrl)"
              target="_blank"
              class="msg-result-btn"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              <span>{{ downloadText }} {{ imgIdx + 1 }}</span>
            </a>
          </div>
        </div>

        <!-- 结果气泡：失败 -->
        <div v-else-if="entry.status === 'failed'" class="msg-result msg-error">
          <div class="msg-meta">
            <span class="msg-meta-model" v-if="entry.modelName">{{ entry.modelName }}</span>
            <span class="msg-meta-prompt" v-if="entry.prompt">{{ entry.prompt }}</span>
          </div>
          <div class="msg-error-text">{{ entry.error || failedText }}</div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div class="img-preview-overlay" v-if="previewVisible" @click="previewVisible = false">
      <img :src="previewUrl" alt="" class="img-preview-large" @click.stop />
    </div>
  </div>
</template>

<script>
var proxifyVolcArkTosFetchUrl = require('../../api/ai/official/volc-ark-tos-fetch-url').proxifyVolcArkTosFetchUrl

export default {
  name: 'GenerationHistory',
  props: {
    items: {
      type: Array,
      default: function () { return [] }
    },
    type: {
      type: String,
      default: 'video'
    },
    generating: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      previewVisible: false,
      previewUrl: ''
    }
  },
  computed: {
    emptyText: function () {
      return this.$t('home.generatorNew.noHistory') || 'Results will appear here'
    },
    generatingText: function () {
      return this.$t('home.generatorNew.generating') || 'Generating...'
    },
    downloadText: function () {
      return this.$t('modelDetail.download') || 'Download'
    },
    failedText: function () {
      return this.$t('home.generatorNew.generationFailed') || 'Generation failed'
    }
  },
  watch: {
    items: {
      handler: function () {
        this.scrollToBottom()
      },
      deep: true
    },
    generating: function () {
      if (this.generating) {
        this.scrollToBottom()
      }
    }
  },
  methods: {
    /** 本地开发：历史里的视频/图直连火山 TOS 会因跨域无法播放或下载，与缓存 fetch 同源走 vue 代理 */
    proxiedMediaUrl: function (url) {
      var s = String(url || '').trim()
      if (!s) return ''
      if (s.indexOf('blob:') === 0 || s.indexOf('data:') === 0) return s
      return proxifyVolcArkTosFetchUrl(s)
    },
    scrollToBottom: function () {
      var self = this
      self.$nextTick(function () {
        var el = self.$refs.historyContainer
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },
    previewImage: function (url) {
      this.previewUrl = url
      this.previewVisible = true
    },
    getEntryImageUrls: function (entry) {
      if (!entry) return []
      if (Array.isArray(entry.resultUrls) && entry.resultUrls.length) return entry.resultUrls
      if (entry.resultUrl) return [entry.resultUrl]
      return []
    },
    getMusicTrackUrls: function (entry) {
      if (!entry) return []
      var trimStr = function (x) { return String(x || '').trim() }
      var splitComma = function (raw) {
        var t = trimStr(raw)
        if (!t) return []
        return t.split(',').map(function (p) { return trimStr(p) }).filter(Boolean)
      }
      var flat = []
      if (Array.isArray(entry.resultUrls) && entry.resultUrls.length) {
        for (var i = 0; i < entry.resultUrls.length; i++) {
          var part = trimStr(entry.resultUrls[i])
          if (!part) continue
          if (part.indexOf(',') !== -1) {
            var segs = splitComma(part)
            for (var j = 0; j < segs.length; j++) flat.push(segs[j])
          } else {
            flat.push(part)
          }
        }
      }
      if (!flat.length) {
        flat = splitComma(entry.resultUrl)
      }
      var seen = {}
      var out = []
      for (var k = 0; k < flat.length; k++) {
        var u = flat[k]
        if (!u || seen[u]) continue
        seen[u] = true
        out.push(u)
      }
      return out
    },
    /** 仅 Suno：侧栏用 msg-music-cell（多轨 audio）。勿用 mediaType / 后缀推断，避免其它渠道 mp3 走音乐样式 */
    isHistoryMusic: function (entry) {
      if (!entry) return false
      var name = String(entry.modelName || '').trim().toLowerCase()
      if (name.indexOf('suno') !== -1) return true
      if (name === 'ai music') return true
      return false
    }
  }
}
</script>

<style scoped>
.msg-music-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: stretch;
  margin-top: 4px;
}
.msg-music-row-single .msg-music-cell {
  flex: 1 1 100%;
  max-width: 100%;
}
.msg-music-row:not(.msg-music-row-single) .msg-music-cell {
  flex: 1 1 0;
  min-width: 0;
}
.msg-music-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.msg-music-audio {
  width: 100%;
  height: 36px;
}
.msg-music-dl {
  align-self: flex-start;
}
.msg-media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.msg-media-thumb-grid {
  width: 100%;
  height: 120px;
  object-fit: cover;
}
</style>
